@if (errorMessage()) {
  <div class="errorMessage">
    {{ errorMessage() }}
  </div>
}
<div class="toolbar">
  <button (click)="backToEntity()" [disabled]="isBusy()" matButton="text">
    <span>
      <mat-icon class="size-16">keyboard_arrow_left</mat-icon>
      <span>Back</span>
    </span>
  </button>
</div>
<div class="tab-content">
  @if (isBusy()) {
    <mat-progress-spinner mode="indeterminate" diameter="32"></mat-progress-spinner>
  }
  @if (report(); as r) {
    <div class="autoCostWrapper">
      <h3 class="reportTitle">Auto-Cost Report {{ r.entityId }}-{{ r.revision }}</h3>
      @if (r.warmingUp) {
        <div class="warmingUp">Model warming up: estimates are unavailable for a few minutes. Engineered and estimated lines will show once it finishes.</div>
      } @else {
        <div class="modelInfo">
          Model run {{ r.modelRunDate | date: 'medium' }} ({{ r.modelVersion }}), trained on builds through {{ r.trainEnd | date: 'mediumDate' }}
        </div>
      }
      <div class="totalsRow">
        <div class="totalItem">
          <span class="totalLabel">Estimated cost</span>
          <span class="totalValue">{{ r.estimateTotal | currency: 'USD' }}</span>
        </div>
        <div class="totalItem">
          <span class="totalLabel">80% range</span>
          <span class="totalValue">{{ r.low80Total | currency: 'USD' }} – {{ r.high80Total | currency: 'USD' }}</span>
        </div>
        <div class="totalItem">
          <span class="totalLabel">95% range</span>
          <span class="totalValue">{{ r.low95Total | currency: 'USD' }} – {{ r.high95Total | currency: 'USD' }}</span>
        </div>
        @if (r.linesWithoutEstimate > 0) {
          <div class="totalItem warning">
            {{ r.linesWithoutEstimate }} {{ r.linesWithoutEstimate === 1 ? 'line' : 'lines' }} without an estimate
          </div>
        }
      </div>
      <table class="lineSkuTable">
        <thead>
          <tr>
            <th class="textCell">Line</th>
            <th class="textCell">PRN</th>
            <th class="numCell">Qty</th>
            <th class="numCell">Est./unit</th>
            <th class="numCell">80% low</th>
            <th class="numCell">80% high</th>
            <th class="numCell">95% low</th>
            <th class="numCell">95% high</th>
            <th class="numCell">Ext. estimate</th>
            <th class="textCell">Risk</th>
            <th class="textCell">SFRs</th>
            <th class="numCell">Quoted/unit</th>
            <th class="numCell">Cost/price</th>
            <th class="textCell">Notes</th>
          </tr>
        </thead>
        <tbody>
          @for (line of r.lines; track line.lineId) {
            <tr>
              <td class="textCell">{{ line.lineId }}</td>
              <td class="textCell" [matTooltip]="line.prn">{{ truncatePrn(line.prn) }}</td>
              <td class="numCell">{{ line.quantity }}</td>
              @if (line.noEstimateReason) {
                <td class="noEstimate" colspan="6">No estimate — {{ humanise(line.noEstimateReason) }}</td>
              } @else if (isEngineered(line)) {
                <td class="numCell">
                  {{ line.estimate | currency: 'USD' }}
                  <span class="engineered" [matTooltip]="'PRMS standard cost of ' + line.mfgNumber">actual</span>
                </td>
                <td class="numCell engineeredBand" colspan="4">Engineered as {{ line.mfgNumber }}</td>
                <td class="numCell">{{ extendedEstimate(line) | currency: 'USD' }}</td>
              } @else {
                <td class="numCell">{{ line.estimate | currency: 'USD' }}</td>
                <td class="numCell">{{ line.low80 | currency: 'USD' }}</td>
                <td class="numCell">{{ line.high80 | currency: 'USD' }}</td>
                <td class="numCell">{{ line.low95 | currency: 'USD' }}</td>
                <td class="numCell">{{ line.high95 | currency: 'USD' }}</td>
                <td class="numCell">{{ extendedEstimate(line) | currency: 'USD' }}</td>
              }
              <td class="textCell">
                @if (line.risk && !isEngineered(line)) {
                  <span class="riskBadge risk{{ line.risk }}">{{ line.risk }}</span>
                }
              </td>
              <td class="textCell">
                <span [matTooltip]="sfrTooltip(line)" matTooltipClass="multiLineTooltip">{{ line.sfrCount }}</span>
                @if (line.unseenSfrCount > 0) {
                  <span class="unseenSfr">{{ line.unseenSfrCount }} never built</span>
                }
              </td>
              <td class="numCell">
                @if (line.quotedUnitPrice) {
                  {{ line.quotedUnitPrice | currency: 'USD' }}
                }
              </td>
              <td class="numCell">
                @if (costToPricePercent(line) !== null && !line.noEstimateReason) {
                  {{ costToPricePercent(line)! | number: '1.0-1' }}%
                }
              </td>
              <td class="textCell">
                @for (warning of line.warnings || []; track warning.code) {
                  @if (warning.code !== 'NO_ESTIMATE') {
                    <div class="lineWarning">{{ warning.message }}</div>
                  }
                }
              </td>
            </tr>
          }
        </tbody>
      </table>
      <div class="footnote">
        80% / 95% bands are calibrated on held-out builds; an SFR that has never been built adds nothing to the estimate but widens its band.
        Lines marked actual are already engineered: their cost is that unit's PRMS standard cost, SFR work included.
      </div>
    </div>
  }
</div>
